import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function StickyBanner({ onDemoClick }) {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Restore dismissed state across the session
    if (sessionStorage.getItem('mp_banner_dismissed') === '1') {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      // Show banner after user scrolls past the hero section (~700px)
      // Hide it near the bottom of the page so it doesn't overlap the footer CTA
      const scrolled = window.scrollY;
      const nearBottom =
        window.innerHeight + scrolled >= document.documentElement.scrollHeight - 600;
      setVisible(scrolled > 700 && !nearBottom);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    sessionStorage.setItem('mp_banner_dismissed', '1');
  };

  if (dismissed) return null;

  const copy = {
    es: {
      eyebrow: 'Oferta de lanzamiento',
      title: 'Activa tu negocio hoy',
      sub: 'Activa MatchPool.live en tu negocio',
      cta: 'Aplicar ahora',
      social: 'negocios solicitaron activar sus negocios esta semana',
    },
    en: {
      eyebrow: 'Launch offer',
      title: 'Activate your business today',
      sub: 'Activate MatchPool.live in your business',
      cta: 'Apply now',
      social: 'businesses requested to activate their businesses this week',
    },
  }[lang];

  // Deterministic-ish weekly counter that gently grows day by day (12–28 range)
  const leadsCount = (() => {
    const base = 12;
    const day = new Date().getDay(); // 0..6
    return base + day * 2 + ((new Date().getDate() % 5));
  })();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed bottom-5 left-3 right-20 sm:left-6 sm:right-auto z-40
            sm:w-auto sm:max-w-lg"
          data-testid="sticky-demo-banner">
          <div className="relative flex items-center gap-3 sm:gap-4 pl-4 pr-3 sm:pl-5 sm:pr-4 py-3
            rounded-2xl backdrop-blur-xl
            bg-gradient-to-r from-[#0F6B49]/95 via-[#13191f]/95 to-[#13191f]/95
            border border-[#D5A63B]/40
            shadow-[0_10px_40px_-10px_rgba(213,166,59,0.45),0_0_0_1px_rgba(15,107,73,0.3)]">
            {/* Pulsing accent dot */}
            <div className="hidden sm:flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-xl
              bg-[#D5A63B]/15 border border-[#D5A63B]/30">
              <Sparkles size={18} className="text-[#D5A63B]" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#D5A63B]
                  font-[Inter]">
                  {copy.eyebrow}
                </span>
                <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-[#D5A63B]/60" />
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-[#F3F1EB]/50
                  font-[Inter]">
                  {copy.sub}
                </span>
              </div>
              <p className="text-[#F3F1EB] text-sm sm:text-base font-semibold leading-tight mt-0.5
                font-[Inter]">
                {copy.title}
              </p>
              <div className="flex items-center gap-1.5 mt-1" data-testid="sticky-demo-social-proof">
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22c55e]" />
                </span>
                <p className="text-[11px] sm:text-xs text-[#F3F1EB]/70 font-[Inter] leading-tight truncate">
                  <span className="font-bold text-[#F3F1EB]">{leadsCount}</span>{' '}{copy.social}
                </p>
              </div>
            </div>

            <button
              onClick={onDemoClick}
              className="flex-shrink-0 bg-[#D5A63B] hover:bg-[#e8b845] text-[#1F2833]
                text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-xl
                transition-colors duration-200 whitespace-nowrap shadow-[0_4px_14px_rgba(213,166,59,0.4)]"
              data-testid="sticky-demo-cta">
              {copy.cta}
            </button>

            <button
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="flex-shrink-0 w-7 h-7 rounded-lg text-[#F3F1EB]/60 hover:text-[#F3F1EB]
                hover:bg-white/5 flex items-center justify-center transition-colors duration-150"
              data-testid="sticky-demo-dismiss">
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
